import { useState } from 'react';
import {
  AgxButton,
  AgxGlyph,
  AgxSpacer,
  AgxTypography,
  Icon_Arrow,
  Icon_Reverse,
} from '@urbanx/agx.ui-components2';
import { CampaignStageAction } from 'Api/Campaigns/Types/campaignTypes';
import { selectContract, usePerformCampaignAction } from 'features/campaigns/campaignsReducer';
import { Tabs } from 'types/Tabs';
import './FormDetailsHeader.scss';
import {
  CampaignDetailModelStage,
  CampaignDetailModelState,
  EnvelopeSubmissionModelStatus,
  InProgressFormModelFormType,
} from '@urbanx/agx.campaigns.client';
import type { CampaignDetailModel } from '@urbanx/agx.campaigns.client';
import dayjs from 'dayjs';
import useStateFormLabels from 'features/form/FormLabels/useStateFormLabels';
import type { MarketingPackageProjection } from '@urbanx/agx.marketing.client';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getSubmissionTypeText } from '../AgencyAgreement/AgencyAgreementSigningPanel.helpers';

type SubmissionType = Parameters<typeof getSubmissionTypeText>[0];

interface FormDetailsHeaderProps {
  id?: string;
  campaign: CampaignDetailModel;
  currentTab?: Tabs;
  selectedMarketingPackage?: MarketingPackageProjection;
  onMarketingPackageBack?: () => void;
}

const FormDetailsHeader = ({
  id,
  campaign,
  currentTab,
  selectedMarketingPackage,
  onMarketingPackageBack,
}: FormDetailsHeaderProps) => {
  const performCampaignAction = usePerformCampaignAction();
  const selectedContract = useNewSelectedContract();
  const dispatch = useAppDispatch();

  const [disableArchiveButton, setDisableArchiveButton] = useState(false);

  const {
    campaignId,
    stage: campaignStage,
    state,
    inProgressForms,
    agencyAgreementEnvelope,
    lastUpdatedUtc,
  } = campaign;

  const validInProgressForms = inProgressForms?.filter((ipf) => ipf != null);

  const inProgressForm =
    campaignStage === CampaignDetailModelStage.AgreementSubmissionIncomplete
      ? validInProgressForms?.find(
          (ipf) => ipf.formType === InProgressFormModelFormType.ListingSubmission
        )
      : validInProgressForms?.find(
          (ipf) => ipf.formType === InProgressFormModelFormType.ListingGenerator
        );

  const { lastUpdateUtc } = inProgressForm ?? {};

  const formName = useStateFormLabels(state ?? CampaignDetailModelState.QLD, 'formName');

  const contractSent =
    selectedContract?.envelopeSubmission?.status &&
    [
      EnvelopeSubmissionModelStatus.Signed,
      EnvelopeSubmissionModelStatus.Complete,
      EnvelopeSubmissionModelStatus.Sent,
    ].includes(selectedContract.envelopeSubmission.status);

  const agreementSigningStages = [
    CampaignDetailModelStage.AgreementAgentSigning,
    CampaignDetailModelStage.AgreementVendorSigning,
    CampaignDetailModelStage.AgreementSignedByAllParties,
  ];

  const dateDetail = (label: 'Last updated' | 'Sent', date: Date | string | undefined) =>
    date ? `${label} ${dayjs(date).format('DD/MM/YY')}` : 'Not yet updated';

  const headerConfig = (() => {
    if (campaignStage === CampaignDetailModelStage.ProposalDraft) {
      return { title: 'Proposal', detail: 'Draft', onBack: undefined };
    }

    if (selectedMarketingPackage) {
      return {
        title: 'Marketing',
        detail: dateDetail('Last updated', selectedMarketingPackage.lastUpdatedUtc),
        onBack: onMarketingPackageBack,
      };
    }

    if (selectedContract) {
      return {
        title: 'Contracts',
        detail: dateDetail(contractSent ? 'Sent' : 'Last updated', selectedContract.lastUpdateUtc),
        onBack: () => dispatch(selectContract(null)),
      };
    }

    if (
      campaignStage &&
      agreementSigningStages.includes(campaignStage) &&
      agencyAgreementEnvelope
    ) {
      return {
        title: formName,
        detail: `Sent via ${getSubmissionTypeText(
          agencyAgreementEnvelope.type as SubmissionType
        )} on ${dayjs(agencyAgreementEnvelope.createdUtc).format('DD/MM/YY')}`,
        onBack: undefined,
      };
    }

    if (
      campaignStage === CampaignDetailModelStage.AgreementPendingCompliance ||
      campaignStage === CampaignDetailModelStage.ListingCurrent ||
      campaignStage === CampaignDetailModelStage.ListingUnderContract ||
      campaignStage === CampaignDetailModelStage.ListingUnconditional ||
      campaignStage === CampaignDetailModelStage.ListingWithdrawn ||
      campaignStage === CampaignDetailModelStage.ListingSold
    ) {
      return {
        title: 'Listing',
        detail: dateDetail('Last updated', lastUpdatedUtc),
        onBack: undefined,
      };
    }

    return {
      title: formName,
      detail: dateDetail('Last updated', lastUpdateUtc),
      onBack: undefined,
    };
  })();

  const performAction = async (action: CampaignStageAction) => {
    if (!campaignId) return;

    await performCampaignAction({
      campaignId,
      campaignAction: action,
    });

    setDisableArchiveButton(false);
  };

  const onArchiveCampaign = () => {
    if (disableArchiveButton) return;
    setDisableArchiveButton(true);
    performAction(CampaignStageAction.Archive);
  };

  const onRestoreCampaign = () => {
    performAction(CampaignStageAction.Restore);
  };

  const isCampaignStageValid = campaignStage === CampaignDetailModelStage.AgreementDraft;

  const shouldRenderRestore = isCampaignStageValid && currentTab === Tabs.Archive;
  const shouldRenderArchive =
    isCampaignStageValid && currentTab !== Tabs.Archive && currentTab !== Tabs.Listings;

  const title = (
    <AgxTypography
      id={`agx-${id}-form-name`}
      type="caption"
      size="medium"
      dataTestId={`agx-${id}-form-name`}
      color="var(--text-tertiary)"
    >
      {headerConfig.title}
    </AgxTypography>
  );

  const backButton = !!headerConfig.onBack && (
    <button
      type="button"
      className="formDetailsBack"
      onClick={headerConfig.onBack}
      aria-label="Back"
    >
      <Icon_Arrow
        direction="left"
        data-testid={`agx-${id}-arrow-back`}
        height={18}
        width={18}
        color="var(--text-tertiary)"
      />
    </button>
  );

  const detailSection = (
    <AgxTypography
      id={`agx-${id}-date`}
      type="caption"
      size="medium"
      color="var(--text-tertiary)"
      dataTestId={`agx-${id}-date`}
    >
      {headerConfig.detail}
    </AgxTypography>
  );

  return (
    <>
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
        className="formDetailsHeaderMobile"
      >
        {shouldRenderRestore && (
          <button
            type="button"
            className="restore"
            onClick={onRestoreCampaign}
            aria-label="Restore"
          >
            <Icon_Reverse height={18} width={18} color="var(--text-tertiary)" />
          </button>
        )}
        {shouldRenderArchive && !disableArchiveButton && (
          <button
            type="button"
            className="archive"
            onClick={onArchiveCampaign}
            aria-label="Archive"
          >
            <AgxGlyph type="box" height={18} width={18} />
          </button>
        )}
        {backButton}
        <AgxSpacer
          contentJustification="center"
          contentAlignment="center"
          className="formDetailsTitleLastUpdated"
        >
          {title}
          {detailSection}
        </AgxSpacer>
      </AgxSpacer>
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
        className="formDetailsHeaderDesktop"
      >
        <AgxSpacer
          direction="horizontal"
          gap="spacing-xs"
          contentAlignment="center"
          className="formDetailsTitleInfo"
          onClick={headerConfig.onBack}
        >
          {backButton}
          {title}
        </AgxSpacer>
        {detailSection}
        {shouldRenderRestore && (
          <AgxButton
            id={`agx-${id}-restore`}
            variant="naked"
            size="small"
            text="Restore"
            minimal
            onClick={onRestoreCampaign}
          />
        )}
        {shouldRenderArchive && (
          <AgxButton
            id={`agx-${id}-archive`}
            variant="naked"
            size="small"
            text="Archive"
            disabled={disableArchiveButton}
            minimal
            onClick={onArchiveCampaign}
          />
        )}
      </AgxSpacer>
    </>
  );
};

export default FormDetailsHeader;
